import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Plus, Edit, Trash2, Eye, EyeOff, Loader2, LogOut } from 'lucide-react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AdminResources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const { logout } = useAdminAuth();

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/resources`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch resources');
      }

      const data = await response.json();
      setResources(data);
    } catch (err) {
      console.error('Error fetching resources:', err);
      toast.error('Failed to fetch resources');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    setDeletingId(id);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/resources/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete resource');
      }

      toast.success('Resource deleted successfully');
      setResources(resources.filter(r => r.id !== id));
    } catch (err) {
      console.error('Error deleting resource:', err);
      toast.error('Failed to delete resource');
    } finally {
      setDeletingId(null);
    }
  };

  const togglePublish = async (resource) => {
    const newStatus = resource.status === 'published' ? 'draft' : 'published';
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/resources/${resource.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update resource');
      }

      const updatedResource = await response.json();
      setResources(resources.map(r => r.id === resource.id ? updatedResource : r));
      toast.success(`Resource ${newStatus === 'published' ? 'published' : 'unpublished'} successfully`);
    } catch (err) {
      console.error('Error updating resource:', err);
      toast.error('Failed to update resource');
    }
  };

  const typeLabels = {
    'blog': 'Blog',
    'case-study': 'Case Study',
    'rnd': 'R&D',
    'soft-paper': 'Soft Paper',
    'pdf': 'PDF'
  };

  const sourceLabels = {
    'internal': 'Internal',
    'external-url': 'External',
    'pdf': 'PDF'
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 p-6 lg:p-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Resources Admin</h1>
            <p className="text-gray-600">Manage your resources, case studies, and content</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={logout}
              variant="outline"
              className="bg-white hover:bg-gray-100 text-gray-900 border-gray-3000"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
            <Link to="/admin/resource-form">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white">
                <Plus className="w-4 h-4 mr-2" />
                New Resource
              </Button>
            </Link>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="ml-3 text-gray-600">Loading resources...</span>
          </div>
        )}

        {/* Resources Table */}
        {!loading && (
          <>
            {resources.length > 0 ? (
              <div className="bg-white border border-gray-30000 rounded-lg overflow-hidden backdrop-blur-sm">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white border-b border-gray-30000">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Title</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Type</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Source</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Updated</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resources.map((resource) => (
                        <tr key={resource.id} className="border-b border-gray-5 hover:bg-white transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-gray-900">{resource.title}</div>
                              <div className="text-sm text-gray-600 line-clamp-1">{resource.shortDescription}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-500 border border-blue-500/30">
                              {typeLabels[resource.type]}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-600">{sourceLabels[resource.source]}</td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              resource.status === 'published' 
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                            }`}>
                              {resource.status === 'published' ? 'Published' : 'Draft'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-600 text-sm">
                            {format(new Date(resource.updatedAt), 'MMM dd, yyyy')}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => togglePublish(resource)}
                                className="bg-white hover:bg-gray-100 text-gray-900 border-gray-3000"
                              >
                                {resource.status === 'published' ? (
                                  <EyeOff className="w-4 h-4" />
                                ) : (
                                  <Eye className="w-4 h-4" />
                                )}
                              </Button>
                              <Link to={`/admin/resource-form/${resource.id}`}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-white hover:bg-gray-100 text-gray-900 border-gray-3000"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDelete(resource.id, resource.title)}
                                disabled={deletingId === resource.id}
                                className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/30"
                              >
                                {deletingId === resource.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-20 bg-white border border-gray-30000 rounded-lg backdrop-blur-sm">
                <p className="text-gray-600 text-lg mb-4">No resources yet.</p>
                <Link to="/admin/resources/new">
                  <Button className="bg-blue-500 hover:bg-blue-600 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Resource
                  </Button>
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminResources;
